import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { MoreActionsMenu } from "../CampaignConstants";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState } from "react";
import campaignStyles from "../Campaigns.styles";
import RenameModal from "./RenameModal";
import DeleteCampaignModal from "./DeleteCampaignModal";
import viewpaths from "global/constants/ViewPathConstants";
import { CampaignDetails } from "models/interfaces";
import history from "utils/history";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";
import RetargetIcon from "assets/images/retarget.svg";
import { campaignStatusEnum, campaignTypesEnum } from "../Constants";
import DuplicateModal from "./DuplicateModal";

interface CustomProps {
  campaignDetails: CampaignDetails[];
  setCampaign: Function;
  refresh: Function;
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case "Completed":
      return {
        color: "#47AA12",
        backgroundColor: "#F7FFF2",
      };
    case "Submitted":
      return {
        color: "#FF9436",
        backgroundColor: "#FFF6E2",
      };
    case "Draft":
      return {
        color: "#475569",
        backgroundColor: "#EFEEEA",
      };
    case "Failed":
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case "InProgress":
      return {
        color: "#125DDB",
        backgroundColor: "#E8EFFC",
      };
    case "Rejected":
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case "InReview":
      return {
        color: "#00B2FF",
        backgroundColor: "#00B2FF0F",
      };
    default:
      return {
        color: "#6B91FC",
        backgroundColor: "#F3F7FF",
      };
  }
};

const EmailDataList = (props: CustomProps) => {
  const theme = useTheme();
  const classes = campaignStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [moreOptionsMetadata, setMoreOptionsMetadata] = useState<{
    type: string;
    status: string;
  }>({ status: "all", type: "" });
  const [campaignId, setCampaignId] = useState<number>(0);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenMenu = (
    event: any,
    status: string,
    type: string,
    id: number
  ) => {
    setAnchorEl(event.currentTarget);
    setMoreOptionsMetadata({
      type,
      status,
    });
    setCampaignId(id);
  };

  const handleMenuItemClick = async (action: string, campaignId: number) => {
    switch (action) {
      case "view":
        setAnchorEl(null);
        history.push(`${viewpaths.CampaignStatsViewPath}?id=${campaignId}`);
        break;
      case "edit":
        if (moreOptionsMetadata.type === "ABTesting") {
          history.push(
            `${viewpaths.createABTestCampaignViewPath}?id=${campaignId}`
          );
        } else
          history.push(
            `${viewpaths.createRegularCampaignViewPath}?id=${campaignId}`
          );
        break;
      case "rename":
        setAnchorEl(null);
        setRenameModalOpen(true);
        break;
      case "duplicate":
        setAnchorEl(null);
        setDuplicateModalOpen(true);
        break;
      case "delete":
        setAnchorEl(null);
        setDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const moreOptions = () => {
    const menu = MoreActionsMenu(moreOptionsMetadata);
    if (moreOptionsMetadata.type === "Regular") {
      menu.push({
        label: "Duplicate",
        value: "duplicate",
      });
    }
    return (
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {menu.map((item, index) => (
          <MenuItem
            key={index}
            value={item.value}
            sx={classes.dropDownMenuItem}
            onClick={() => handleMenuItemClick(item.value, campaignId)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const getPercentage = (count: number, divideby: number) => {
    return ((count / (divideby === 0 ? 1 : divideby)) * 100).toFixed();
  };

  const getCampaignRedirection = (campaign: CampaignDetails) => {
    if (campaign.type !== "ABTesting") {
      if (campaign.status === "Draft" || campaign.status === "Submitted") {
        history.push(
          `${viewpaths.createRegularCampaignViewPath}?id=${campaign.id}`
        );
      } else if (campaign.status === "InReview") {
        return false;
      } else {
        setCampaignId(campaign.id);
        history.push(`${viewpaths.CampaignStatsViewPath}?id=${campaign.id}`);
      }
    } else if (campaign.type === "ABTesting") {
      if (campaign.status === "Draft") {
        history.push(
          `${viewpaths.createABTestCampaignViewPath}?id=${campaign.id}`
        );
      } else {
        setCampaignId(campaign.id);
        history.push(`${viewpaths.CampaignStatsViewPath}?id=${campaign.id}`);
      }
    }
  };

  const getQuickStats = (campaign: CampaignDetails) => {
    return (
      <>
        <Tooltip
          title="The percentage of unique recipients who opened the email."
          placement="bottom"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                width: "auto",
                background: theme.palette.primary.dark,
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
              },
            },
            arrow: {
              sx: {
                "&::before": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            },
          }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Typography variant="h5" color="primary">
              {getPercentage(
                campaign.metrics.find((item) => item.metricId === 5)?.count ||
                  0,
                campaign.metrics.find((item) => item.metricId === 3)?.count || 0
              )}
              %
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Open rate</Typography>
            </Stack>
          </Stack>
        </Tooltip>
        <Tooltip
          title="The percentage of recipients who clicked on at least one link within the email."
          placement="bottom"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                width: "auto",
                background: theme.palette.primary.dark,
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
              },
            },
            arrow: {
              sx: {
                "&::before": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            },
          }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Typography variant="h5" color="primary">
              {getPercentage(
                campaign.metrics.find((item) => item.metricId === 6)?.count ||
                  0,
                campaign.metrics.find((item) => item.metricId === 3)?.count || 0
              )}
              %
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Click rate</Typography>
            </Stack>
          </Stack>
        </Tooltip>
        <Tooltip
          title="The percentage of recipients who opened the email and clicked on a link."
          placement="bottom"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                width: "auto",
                background: theme.palette.primary.dark,
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
              },
            },
            arrow: {
              sx: {
                "&::before": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            },
          }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Typography variant="h5" color="primary">
              {getPercentage(
                campaign.metrics.find((item) => item.metricId === 6)?.count ||
                  0,
                campaign.metrics.find((item) => item.metricId === 5)?.count || 0
              )}
              %
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">CTOR</Typography>
            </Stack>
          </Stack>
        </Tooltip>
      </>
    );
  };

  const getCampaignDetails = (campaign: CampaignDetails) => {
    return (
      <>
        <Typography
          variant="body2"
          color={theme.palette.primary.dark}
          gutterBottom
        >
          Created by {campaign.campaignerEmail}
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Typography variant="body2" sx={{ color: "#47556980" }} gutterBottom>
            {campaignTypesEnum[campaign.type]} Campaign
          </Typography>
          <FiberManualRecordIcon
            fontSize="small"
            sx={{
              width: "8px",
              color: "#47556980",
              display: { xs: "none", md: "block" },
            }}
          />
          <Typography variant="body2" sx={{ color: "#47556980" }} gutterBottom>
            Scheduled on{" "}
            {convertESTtoUserLocalDateAndTime(
              campaign.scheduleTime,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        </Stack>
      </>
    );
  };

  const getMobileVersionCard = (campaign: CampaignDetails) => {
    return (
      <Stack direction="column" justifyContent="space-between">
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={campaign.name} arrow>
                <Typography
                  gutterBottom
                  variant="h6"
                  noWrap
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="300px"
                  display="block"
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => getCampaignRedirection(campaign)}
                >
                  {campaign.name}
                </Typography>
              </Tooltip>
              {campaign.type === "Regular" && campaign.haveChildren && (
                <Tooltip title="You have used this campaign to retarget.">
                  <img src={RetargetIcon} height="20px" />
                </Tooltip>
              )}
            </Stack>
            <IconButton
              size="small"
              sx={{
                color: theme.palette.primary.dark,
                padding: 0,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                handleOpenMenu(e, campaign.status, campaign.type, campaign.id);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>
          <Box>
            <Chip
              label={campaignStatusEnum[campaign.status] ?? campaign.status}
              sx={{
                ...getStatusColor(campaign.status),
                borderRadius: "5px",
                minWidth: "90px",
              }}
              size="medium"
            />
          </Box>
          {getCampaignDetails(campaign)}
        </Stack>
        <Stack
          direction="row"
          spacing={4}
          mt={2}
          alignItems="center"
          justifyContent="space-around"
        >
          {getQuickStats(campaign)}
        </Stack>
      </Stack>
    );
  };

  const getDesktopVersionCard = (campaign: CampaignDetails) => {
    return (
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title={campaign.name} arrow>
              <Typography
                gutterBottom
                variant="h6"
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxWidth="250px"
                display="block"
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => getCampaignRedirection(campaign)}
              >
                {campaign.name}
              </Typography>
            </Tooltip>
            <Chip
              label={campaignStatusEnum[campaign.status] ?? campaign.status}
              sx={{
                ...getStatusColor(campaign.status),
                borderRadius: "5px",
                minWidth: "90px",
              }}
              size="medium"
            />
            {campaign.type === "Regular" && campaign.haveChildren && (
              <Tooltip title="You have used this campaign to retarget.">
                <img src={RetargetIcon} height="20px" />
              </Tooltip>
            )}
          </Stack>
          {getCampaignDetails(campaign)}
        </Box>
        <Stack direction="row" spacing={4} mt={1} alignItems="center">
          {getQuickStats(campaign)}
          <IconButton
            size="small"
            sx={{
              color: theme.palette.primary.dark,
              padding: 0,
            }}
            onClick={(e: any) => {
              e.stopPropagation();
              handleOpenMenu(e, campaign.status, campaign.type, campaign.id);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Stack>
    );
  };

  const campaignCard = (campaign: CampaignDetails) => {
    return (
      <Card variant="outlined" sx={{ marginBottom: 2 }}>
        <CardContent>
          {isDesktop
            ? getDesktopVersionCard(campaign)
            : getMobileVersionCard(campaign)}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Grid container>
        {props.campaignDetails.map((campaign, index) => (
          <Grid item xs={12} key={index}>
            {campaignCard(campaign)}
          </Grid>
        ))}
      </Grid>
      {moreOptions()}
      <RenameModal
        isOpen={isRenameModalOpen}
        setOpen={setRenameModalOpen}
        campaignId={campaignId}
        refresh={props.refresh}
      />
      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        setOpen={setDuplicateModalOpen}
        campaignId={campaignId}
        refresh={props.refresh}
      />
      <DeleteCampaignModal
        isVisible={isDeleteModalOpen}
        setVisible={setDeleteModalOpen}
        campaignId={campaignId}
        refresh={props.refresh}
      />
    </>
  );
};

export default EmailDataList;
